<template>
  <div
    class="frame-renderer"
    ref="container"
    :style="containerStyle"
    @mousemove="onMouseMove"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @mouseleave="onMouseLeave"
  >
    <transition name="fade" @after-enter="canvasTransitionComplete = true">
      <div
        v-show="isCanvasReady"
        class="frame-renderer__canvas-wrapper"
        :class="{ invisible: hideCanvas }"
      >
        <canvas ref="canvas" width="400" height="300"></canvas>
      </div>
    </transition>

    <!-- Dimension / hover tooltip overlay -->
    <div
      v-if="tooltipVisible"
      class="frame-renderer__tooltip"
      :style="tooltipStyle"
    >
      {{ tooltipText }}
    </div>

    <!-- Dimension annotations (HTML overlay positioned via 3D projection) -->
    <div v-if="showDimensions" class="frame-renderer__dimensions">
      <div
        v-if="widthLabel"
        class="frame-renderer__dim-label frame-renderer__dim-label--width"
        :style="widthLabelStyle"
      >
        {{ widthLabel }}
      </div>
      <div
        v-if="heightLabel"
        class="frame-renderer__dim-label frame-renderer__dim-label--height"
        :style="heightLabelStyle"
      >
        {{ heightLabel }}
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="frame-renderer__loading">
      <div class="spinner-border spinner-border-sm" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Thumbnail overlay for seamless loading -->
    <div
      v-if="showTransitionOverlay"
      class="centered"
      :style="thumbnailOverlayStyle"
    >
      <img
        :src="thumbnailUrl"
        alt="Loading preview..."
        class="frame-renderer__thumbnail"
        :style="thumbnailImageStyle"
        @load="onThumbnailImageLoad"
      />
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { debounce } from 'lodash'
import {
  loadTexture,
  loadFrameTextures,
  createArtworkPlane,
  createMiteredFrame,
  createHangHardware,
  createStretcherBars,
  createSubstrateBackPanel,
  updateMiteredRailGeometry,
  updateBackLegGeometry,
  createMatBorder,
  createMultiMatBorder,
  createResizeHandles,
  createDimensionLines,
  computeOuterDimensions,
  applyArtworkTextureCover,
  inches,
} from '@/utils/frameGeometry.js'
import {
  setupShadowRenderer,
  setupPCSS,
  setupSceneEnvironment,
  createWallPlane,
  createSingleFrameLighting,
  createTelephotoCamera,
  computeFitDistance,
  WALL_WIDTH,
  WALL_HEIGHT,
  EYE_LEVEL,
  CEILING_HEIGHT,
} from '@/utils/sceneLighting.js'

export default {
  name: 'FrameRenderer',

  props: {
    /** Substrate name (e.g. 'Print', 'Canvas') - affects backing paper, stretchers */
    substrate: {
      type: String,
      default: '',
    },
    /** Artwork width in inches */
    artWidth: {
      type: [Number, String],
      default: 16,
    },
    /** Artwork height in inches */
    artHeight: {
      type: [Number, String],
      default: 20,
    },
    /** URL of the artwork image */
    artworkImageUrl: {
      type: String,
      default: null,
    },
    /**
     * Frame option extra_data from the available-options API.
     * Uses top_image for texturing. Expected shape: { top_image, width, rabbet, ... }
     * Pass null for no frame.
     */
    frameData: {
      type: Object,
      default: null,
    },
    /**
     * Mat option extra_data.
     * Expected shape: { color, ... }
     * Pass null for no mat.
     */
    matData: {
      type: Object,
      default: null,
    },
    /** Multi-mat: array of { color } for Mat 1, Mat 2, Mat 3 (outer to inner) */
    matDataArray: {
      type: Array,
      default: null,
    },
    /** Mat border width in inches. Object { top, bottom, left, right } or single number. */
    matWidth: {
      type: [Number, String, Object],
      default: 2,
    },
    /** Multi-mat: per-mat widths [{ top, bottom, left, right }, ...] */
    matWidthsArray: {
      type: Array,
      default: null,
    },
    /** Container max width in CSS (e.g. '100%', '600px') */
    maxWidth: {
      type: String,
      default: '100%',
    },
    /** Container max height in CSS */
    maxHeight: {
      type: String,
      default: '500px',
    },
    /** Whether interactive features (hover, resize) are enabled */
    interactive: {
      type: Boolean,
      default: true,
    },
    /** Background color of the scene */
    backgroundColor: {
      type: String,
      default: '#f0f0f0',
    },
    /**
     * Hang option data for back-of-frame hardware visualization.
     * Expected shape: { name: 'wire' | 'security hardware' | ... }
     */
    hangData: {
      type: Object,
      default: null,
    },
    /** Stretcher bar depth in inches (Canvas only). When > 0 and substrate is Canvas, show stretchers. */
    stretcherDepth: {
      type: [Number, String],
      default: 0,
    },
    /** Canvas mount style: "Gallery Wrapped", "Frame", or "Float Frame" */
    canvasMount: {
      type: String,
      default: '',
    },
    /** Extra data from Float Frame option (has width for frame face) */
    floatFrameData: {
      type: Object,
      default: null,
    },
    /** Substrate back panel: { depth, color, roughness, metalness } for Aluminum, Acrylic, etc. */
    substrateBackData: {
      type: Object,
      default: null,
    },
    /** Whether to show a wall backdrop behind the frame */
    wallEnabled: {
      type: Boolean,
      default: true,
    },
    /** Wall color (CSS string or hex number) */
    wallColor: {
      type: [String, Number],
      default: '#ffffff',
    },
    /** URL of a pre-rendered thumbnail to show while loading */
    thumbnailUrl: {
      type: String,
      default: null,
    },
    /** Whether the parent is still fetching configuration or options data */
    awaitingConfig: {
      type: Boolean,
      default: false,
    },
    /** Optional shared-element transition name for thumbnail image */
    thumbnailTransitionName: {
      type: String,
      default: '',
    },
    /** Whether Three.js canvas/rendering should be initialized */
    canvasEnabled: {
      type: Boolean,
      default: true,
    },
    /** Whether canvas layer should be visually hidden */
    hideCanvas: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      showThumbnail: true,
      hasInitialFit: false,
      canvasTransitionComplete: false,
      tooltipVisible: false,
      tooltipText: '',
      tooltipX: 0,
      tooltipY: 0,
      showDimensions: false,
      widthLabel: '',
      heightLabel: '',
      widthLabelPos: { x: 0, y: 0 },
      heightLabelPos: { x: 0, y: 0 },
      isDragging: false,
      dragHandle: null,
      dragStartPos: null,
      dragStartDims: null,
      dragWidth: null,
      dragHeight: null,
      flipRotation: 0,
      flipAnimating: false,
    }
  },

  /**
   * Three.js objects are stored directly on the instance (not in data())
   * to avoid Vue reactivity overhead and the _-prefix reservation rule.
   */
  created() {
    this.threeRenderer = null
    this.threeScene = null
    this.threeCamera = null
    this.threeRaycaster = null
    this.threeMouse = null
    this.animFrameId = null
    this.artworkMesh = null
    this.frameGroup = null
    this.matGroup = null
    this.stretcherGroup = null
    this.substrateBackMesh = null
    this.handlesGroup = null
    this.dimGroup = null
    this.sceneContentGroup = null
    this.hangHardwareGroup = null
    this.wallMesh = null
    this.sceneLights = []
    this.builtDims = null
    this.hoveredObject = null
    this._textureCache = null // { artworkUrl, artworkTex, frameKey, frameTextures }
    this.pendingDragDims = null
    this.dragRafId = null
    this.lastEmittedDims = null // { width, height } - avoid duplicate emits
    this.flipZOffset = 0 // current Z translation during flip animation
  },

  computed: {
    containerStyle() {
      return {
        width: '100%',
        maxWidth: this.maxWidth,
        height: 'calc(100vh - 300px)',
        maxHeight: this.maxHeight,
        position: 'relative',
        overflow: 'hidden',
      }
    },
    tooltipStyle() {
      return {
        left: this.tooltipX + 'px',
        top: this.tooltipY + 'px',
      }
    },
    widthLabelStyle() {
      return {
        left: this.widthLabelPos.x + 'px',
        top: this.widthLabelPos.y + 'px',
      }
    },
    heightLabelStyle() {
      return {
        left: this.heightLabelPos.x + 'px',
        top: this.heightLabelPos.y + 'px',
      }
    },
    parsedArtWidth() {
      if (this.dragWidth != null) return this.dragWidth
      return parseFloat(this.artWidth) || 16
    },
    parsedArtHeight() {
      if (this.dragHeight != null) return this.dragHeight
      return parseFloat(this.artHeight) || 20
    },
    frameWidth() {
      return this.frameData ? parseFloat(this.frameData.width) || 0 : 0
    },
    /** Frame depth: match frame width up to 2", default to 1.5" */
    frameDepth() {
      const w = this.frameWidth
      return w > 0 ? Math.min(2, w) : 1.5
    },
    frameRabbet() {
      return this.frameData ? parseFloat(this.frameData.rabbet) || 0 : 0
    },
    parsedMatWidth() {
      const hasMat = this.matData || (this.matDataArray && this.matDataArray.length > 0)
      if (!hasMat) return { top: 0, bottom: 0, left: 0, right: 0 }
      if (typeof this.matWidth === 'object' && this.matWidth !== null) {
        return {
          top: parseFloat(this.matWidth.top) || 0,
          bottom: parseFloat(this.matWidth.bottom) || 0,
          left: parseFloat(this.matWidth.left) || 0,
          right: parseFloat(this.matWidth.right) || 0,
        }
      }
      const w = parseFloat(this.matWidth) || 0
      return { top: w, bottom: w, left: w, right: w }
    },
    /** Max mat width for any side — used for frame offset calculations */
    maxMatWidth() {
      const m = this.parsedMatWidth
      return Math.max(m.top, m.bottom, m.left, m.right)
    },
    hasMatWidth() {
      const m = this.parsedMatWidth
      return m.top > 0 || m.bottom > 0 || m.left > 0 || m.right > 0
    },
    /** Calculates the maximum Z coordinate (closest to camera) of the generated 3D geometry */
    frontZ() {
      const isCanvas = /canvas/i.test(this.substrate || '')
      const isFloatFrame = this.floatFrameData != null && this.floatFrameData.width > 0

      const effectiveFrameWidth = isFloatFrame && this.floatFrameData
        ? (parseFloat(this.floatFrameData.width) || this.frameWidth)
        : this.frameWidth

      const isGalleryWrapped = this.canvasMount?.toLowerCase() === 'gallery wrap'
      const showFrame = !isGalleryWrapped && effectiveFrameWidth > 0

      if (isCanvas) {
        // Canvas front face is always at z=0, frames wrap backwards
        return 0
      }

      if (showFrame) {
        // Standard frames on paper start at z=0 and extrude towards camera by frameDepth
        const frameDepth = Math.min(2, effectiveFrameWidth) || 1.5
        return inches(frameDepth)
      }

      // Paper with mat but no frame: mat sits at z=0 and extrudes outward
      const hasMat = this.matData || (this.matDataArray && this.matDataArray.length > 0) || this.hasMatWidth
      if (hasMat) {
        // createMatBorder uses depth = 0.0625 inches
        return inches(0.0625)
      }

      // Plain paper or otherwise
      return 0
    },
    isCanvasReady() {
      if (this.thumbnailUrl) {
        return !this.showThumbnail
      }
      return this.hasInitialFit && !this.awaitingConfig
    },
    showTransitionOverlay() {
      if (!this.thumbnailUrl) return false
      return !this.canvasTransitionComplete || Boolean(this.thumbnailTransitionName)
    },
    thumbnailOverlayStyle() {
      return {
        viewTransitionName: 'none',
      }
    },
    thumbnailImageStyle() {
      return {
        viewTransitionName: this.thumbnailTransitionName || 'none',
      }
    },
  },

  watch: {
    isCanvasReady(val) {
      if (!val) {
        this.canvasTransitionComplete = false
      }
    },
    artWidth() { if (!this.isDragging) this.debouncedRebuild() },
    artHeight() { if (!this.isDragging) this.debouncedRebuild() },
    artworkImageUrl() { this.debouncedRebuild() },
    substrate() { this.debouncedRebuild() },
    canvasMount() { this.debouncedRebuild() },
    floatFrameData: { handler() { this.debouncedRebuild() }, deep: true },
    substrateBackData: { handler() { this.debouncedRebuild() }, deep: true },
    frameData: { handler() { this.debouncedRebuild() }, deep: true },
    matData: { handler() { this.debouncedRebuild() }, deep: true },
    matDataArray: { handler() { this.debouncedRebuild() }, deep: true },
    matWidthsArray: { handler() { this.debouncedRebuild() }, deep: true },
    matWidth() { this.debouncedRebuild() },
    hangData: { handler() { this.debouncedRebuild() }, deep: true },
    stretcherDepth() { this.debouncedRebuild() },
    backgroundColor() { this.updateBackground() },
    wallEnabled() { this.updateWall() },
    wallColor() { this.updateWall() },
    thumbnailUrl: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.hasInitialFit = false
          this.canvasTransitionComplete = false
          this.$nextTick(() => {
            if (this.threeRenderer) this.fitCamera()
          })
        }
      }
    },
    awaitingConfig(newVal) {
      if (!newVal) {
        // As soon as the config finishes loading, trigger a rebuild to ensure we drop the overlay
        // and finish the layout with the new final options.
        if (this.debouncedRebuild) {
          this.debouncedRebuild()
        }
      }
    },
    canvasEnabled(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.enableCanvasRendering()
        })
      }
    }
  },

  mounted() {
    this.debouncedRebuild = debounce(() => this.rebuildScene(), 150)
    this.rebuildVersion = 0

    // Wait for layout to settle before initializing Three.js
    this.$nextTick(() => {
      if (this.canvasEnabled) {
        this.enableCanvasRendering()
      }
    })

    // Listen for window resize
    this.boundOnResize = () => this.onResize()
    window.addEventListener('resize', this.boundOnResize)

    this.boundHandleDrag = (e) => this.handleDrag(e)
    this.boundEndResizeDrag = () => this.endResizeDrag()
  },

  beforeDestroy() {
    if (this.boundHandleDrag) {
      document.removeEventListener('mousemove', this.boundHandleDrag)
      document.removeEventListener('mouseup', this.boundEndResizeDrag)
    }
    if (this.dragRafId != null) {
      cancelAnimationFrame(this.dragRafId)
      this.dragRafId = null
    }
    // Cancel pending debounced rebuilds
    if (this.debouncedRebuild && this.debouncedRebuild.cancel) {
      this.debouncedRebuild.cancel()
    }
    this.stopRenderLoop()
    if (this.boundOnResize) {
      window.removeEventListener('resize', this.boundOnResize)
    }
    if (this.wallMesh) {
      if (this.wallMesh.geometry) this.wallMesh.geometry.dispose()
      if (this.wallMesh.material) this.wallMesh.material.dispose()
      this.wallMesh = null
    }
    this.sceneLights = []
    if (this.threeRenderer) {
      this.threeRenderer.dispose()
      this.threeRenderer.forceContextLoss()
      this.threeRenderer = null
    }
  },

  methods: {
    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    enableCanvasRendering() {
      if (this.threeRenderer) return
      this.initThree()
      this.rebuildScene()
      this.startRenderLoop()
    },

    initThree() {
      const container = this.$refs.container
      const canvas = this.$refs.canvas
      if (!container || !canvas) return

      const rect = container.getBoundingClientRect()
      const width = Math.max(rect.width, 200)
      const height = Math.max(rect.height, 200)

      // Renderer with soft shadow mapping
      this.threeRenderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        preserveDrawingBuffer: true,
        alpha: false,
      })
      this.threeRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      this.threeRenderer.setSize(width, height)
      this.threeRenderer.outputColorSpace = THREE.SRGBColorSpace
      setupShadowRenderer(this.threeRenderer)
      setupPCSS()

      // Scene
      this.threeScene = new THREE.Scene()
      this.updateBackground()
      setupSceneEnvironment(this.threeRenderer, this.threeScene)

      // Telephoto perspective camera (narrow FOV for flat, compressed look)
      this.threeCamera = createTelephotoCamera(width, height, 18)

      // Lighting: IBL handles ambient; single directional key + spotlight cast PCSS shadows
      this.sceneLights = createSingleFrameLighting({
        centerY: EYE_LEVEL,
        ceilingHeight: CEILING_HEIGHT,
      })
      this.sceneLights.forEach(l => this.threeScene.add(l))

      // Wall backdrop: 12ft × 8ft gallery wall, frame at eye level
      this.updateWall()

      // Raycaster for interactions
      this.threeRaycaster = new THREE.Raycaster()
      this.threeMouse = new THREE.Vector2()
    },

    updateBackground() {
      if (this.threeScene) {
        this.threeScene.background = new THREE.Color(this.backgroundColor)
      }
    },

    updateWall() {
      if (!this.threeScene) return
      // Remove existing wall
      if (this.wallMesh) {
        this.threeScene.remove(this.wallMesh)
        if (this.wallMesh.geometry) this.wallMesh.geometry.dispose()
        if (this.wallMesh.material) this.wallMesh.material.dispose()
        this.wallMesh = null
      }
      if (this.wallEnabled) {
        const zOffset = this.builtDims?.backDepth != null ? this.builtDims.backDepth + 0.01 : 2
        this.wallMesh = createWallPlane(WALL_WIDTH, WALL_HEIGHT, this.wallColor, zOffset, WALL_HEIGHT / 2)
        this.threeScene.add(this.wallMesh)
      }
    },

    // ------------------------------------------------------------------
    // Scene building
    // ------------------------------------------------------------------

    async rebuildScene(opts = {}) {
      if (!this.threeScene) return

      const silent = opts.silent === true
      const version = ++this.rebuildVersion
      if (!silent) this.loading = true

      const isCanvas = String(this.substrate || '').toLowerCase() === 'canvas'
      const canvasMount = String(this.canvasMount || '').trim()
      const isGalleryWrapped = isCanvas && /gallery\s*wrapped/i.test(canvasMount)
      const isFloatFrame = isCanvas && /float\s*frame/i.test(canvasMount)

      const effectiveFrameData = isFloatFrame && this.floatFrameData
        ? this.floatFrameData
        : this.frameData
      const frameKey = effectiveFrameData?.top_image || this.frameData?.top_image || ''

      let artworkTex
      let textures

      try {
        // Use cached textures for silent rebuilds (fast path during drag)
        if (silent && this._textureCache) {
          const c = this._textureCache
          if (c.artworkUrl === this.artworkImageUrl && c.frameKey === frameKey) {
            artworkTex = c.artworkTex
            textures = c.frameTextures
          }
        }
        if (!artworkTex) {
          artworkTex = await loadTexture(this.artworkImageUrl)
          if (version !== this.rebuildVersion) return
        }
        if (!textures && effectiveFrameData && (parseFloat(effectiveFrameData.width) || parseFloat(this.frameWidth))) {
          textures = await loadFrameTextures(effectiveFrameData)
          if (version !== this.rebuildVersion) return
        }
        if (!textures) textures = { front: null, side: null, top: null }

        this._textureCache = {
          artworkUrl: this.artworkImageUrl,
          artworkTex,
          frameKey,
          frameTextures: textures,
        }

        const mw = isGalleryWrapped ? { top: 0, bottom: 0, left: 0, right: 0 } : this.parsedMatWidth
        const floatFrameGap = isFloatFrame ? (parseFloat(this.floatFrameData?.gap) || 0.25) : 0

        const wfBack = this.substrateBackData
        const isWindowFilm = /window\s*film/i.test(this.substrate || '')
        const artworkOpts = isWindowFilm && wfBack ? {
          side: wfBack.useBackface ? THREE.DoubleSide : THREE.FrontSide,
          opacity: wfBack.opacity ?? 1,
          transparent: (wfBack.opacity ?? 1) < 1,
          perforated: wfBack.perforated ?? false,
        } : {}
        const newArtworkMesh = createArtworkPlane(
          this.parsedArtWidth,
          this.parsedArtHeight,
          artworkTex,
          artworkOpts
        )
        newArtworkMesh.renderOrder = 1

        let newMatGroup = null
        const matArr = this.matDataArray || (this.matData ? [this.matData] : [])
        const widthsArr = this.matWidthsArray || (this.hasMatWidth ? [mw] : [])
        if (!isGalleryWrapped && matArr.length > 0 && widthsArr.length > 0) {
          if (matArr.length > 1) {
            newMatGroup = createMultiMatBorder({
              artWidth: this.parsedArtWidth,
              artHeight: this.parsedArtHeight,
              mats: matArr,
              widths: widthsArr,
            })
          } else {
            newMatGroup = createMatBorder({
              artWidth: this.parsedArtWidth,
              artHeight: this.parsedArtHeight,
              matTop: mw.top,
              matBottom: mw.bottom,
              matLeft: mw.left,
              matRight: mw.right,
              color: (matArr[0]?.color || this.matData?.color || '#FFFFFF'),
            })
          }
        }

        let newFrameGroup = null
        const stretcherD = Number(this.stretcherDepth) || 0
        const effectiveFrameWidth = isFloatFrame && this.floatFrameData
          ? (parseFloat(this.floatFrameData.width) || this.frameWidth)
          : this.frameWidth
        const showFrame = !isGalleryWrapped && effectiveFrameData && effectiveFrameWidth > 0
        if (showFrame) {
          // Canvas frame depth matches stretcher assembly depth (75% of bar width) when stretchers present
          const stretcherActualDepth = stretcherD > 0 ? stretcherD * 0.75 : 0
          const frameDepth = isCanvas && stretcherD > 0
            ? stretcherActualDepth
            : Math.min(2, effectiveFrameWidth)
          const innerW = this.parsedArtWidth + mw.left + mw.right + (isFloatFrame ? 2 * floatFrameGap : 0)
          const innerH = this.parsedArtHeight + mw.top + mw.bottom + (isFloatFrame ? 2 * floatFrameGap : 0)
          newFrameGroup = createMiteredFrame({
            artWidth: innerW,
            artHeight: innerH,
            frameWidth: effectiveFrameWidth,
            rabbet: effectiveFrameData.rabbet != null ? parseFloat(effectiveFrameData.rabbet) : this.frameRabbet,
            depth: frameDepth,
            textures,
            isFloatFrame,
          })
          const offsetX = (mw.right - mw.left) / 2
          const offsetY = (mw.top - mw.bottom) / 2
          // Canvas: align frame front with canvas front (both at z=0); Print: frame front at z=d
          const frameZ = isCanvas ? -inches(frameDepth) : 0
          newFrameGroup.position.set(offsetX, offsetY, frameZ)
        }

        let newSubstrateBackMesh = null
        const isMural = /mural/i.test(this.substrate || '')
        const isWindowFilmOpaque = /window\s*film/i.test(this.substrate || '') && this.substrateBackData?.depth > 0
        if (this.substrateBackData && this.substrateBackData.depth > 0 && (!showFrame || isMural || isWindowFilmOpaque)) {
          const sb = this.substrateBackData
          const border = sb.border || 0
          const subW = this.parsedArtWidth + 2 * border
          const subH = this.parsedArtHeight + 2 * border
          newSubstrateBackMesh = createSubstrateBackPanel(subW, subH, sb)
          const sbOffsetX = inches((mw.right - mw.left) / 2)
          const sbOffsetY = inches((mw.top - mw.bottom) / 2)
          newSubstrateBackMesh.position.set(sbOffsetX, sbOffsetY, 0)
        }

        let newStretcherGroup = null
        const depth = stretcherD
        if (isCanvas && depth > 0) {
          newStretcherGroup = createStretcherBars(
            this.parsedArtWidth,
            this.parsedArtHeight,
            depth
          )
        }

        let newHandlesGroup = null
        let newHangHardwareGroup = null
        const innerW = this.parsedArtWidth + mw.left + mw.right
        const innerH = this.parsedArtHeight + mw.top + mw.bottom
        const hwOffsetX = inches((mw.right - mw.left) / 2)
        const hwOffsetY = inches((mw.top - mw.bottom) / 2)
        if (this.hangData) {
          const hangName = this.hangData.name || this.hangData.value || ''
          // Backmost depth: stretchers (if present), else substrate back, else frame depth for canvas, else default
          const frameD = showFrame ? (isCanvas && stretcherD > 0 ? stretcherD * 0.75 : Math.min(2, effectiveFrameWidth)) : 0
          const substrateD = this.substrateBackData?.depth || 0
          const backDepth = isCanvas && stretcherD > 0 ? stretcherD * 0.75
            : (isCanvas && frameD > 0 ? frameD : substrateD > 0 ? substrateD : 0.1)
          // Canvas with stretchers: hardware must stay within stretcher bars (art size). Otherwise use inner (art + mat).
          const hwWidth = isCanvas && stretcherD > 0 ? this.parsedArtWidth : innerW
          const hwHeight = isCanvas && stretcherD > 0 ? this.parsedArtHeight : innerH
          newHangHardwareGroup = createHangHardware({
            innerWidth: hwWidth,
            innerHeight: hwHeight,
            hangType: hangName,
            substrate: this.substrate || '',
            backDepth,
          })
          // Align backing paper with frame opening when mat is asymmetrical
          newHangHardwareGroup.position.set(hwOffsetX, hwOffsetY, 0)
        }

        let newDimGroup = null
        if (this.interactive) {
          newHandlesGroup = createResizeHandles(
            this.parsedArtWidth,
            this.parsedArtHeight
          )
          const dimArtW = isFloatFrame ? this.parsedArtWidth + 2 * floatFrameGap : this.parsedArtWidth
          const dimArtH = isFloatFrame ? this.parsedArtHeight + 2 * floatFrameGap : this.parsedArtHeight
          const substrateBorder = this.substrateBackData?.border ?? 0
          const { outerWidth, outerHeight } = computeOuterDimensions({
            artWidth: dimArtW,
            artHeight: dimArtH,
            matTop: mw.top,
            matBottom: mw.bottom,
            matLeft: mw.left,
            matRight: mw.right,
            frameWidth: showFrame ? effectiveFrameWidth : 0,
            rabbet: this.frameRabbet,
            substrateBorder,
          })
          newDimGroup = createDimensionLines(
            this.parsedArtWidth,
            this.parsedArtHeight,
            (showFrame ? effectiveFrameWidth : 0) + this.maxMatWidth,
            outerWidth,
            outerHeight
          )
        }

        // Swap: remove old, add new in one sync block
        const contentParent = this.sceneContentGroup || this.threeScene
        const toRemove = []
        contentParent.traverse((obj) => {
          if (obj !== this.sceneContentGroup && obj.userData?.type) toRemove.push(obj)
        })
        toRemove.forEach((obj) => {
          if (obj.parent) obj.parent.remove(obj)
          if (obj.geometry) obj.geometry.dispose()
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach(m => m.dispose())
            } else {
              obj.material.dispose()
            }
          }
        })

        if (!this.sceneContentGroup) {
          this.sceneContentGroup = new THREE.Group()
          this.sceneContentGroup.userData = { type: 'content' }
          this.threeScene.add(this.sceneContentGroup)
        }
        this.sceneContentGroup.scale.set(1, 1, 1)
        this.sceneContentGroup.position.set(0, EYE_LEVEL, 0)

        const frameDepthUsed = showFrame
          ? (isCanvas && stretcherD > 0 ? stretcherD * 0.75 : Math.min(2, effectiveFrameWidth))
          : 0
        const hwW = isCanvas && stretcherD > 0 ? this.parsedArtWidth : innerW
        const hwH = isCanvas && stretcherD > 0 ? this.parsedArtHeight : innerH
        const substrateBorder = this.substrateBackData?.border || 0
        this.builtDims = {
          artW: this.parsedArtWidth,
          artH: this.parsedArtHeight,
          substrateBorder,
          innerW,
          innerH,
          backPanelBuiltW: innerW + 2,
          backPanelBuiltH: innerH + 2,
          hwBuiltW: hwW,
          hwBuiltH: hwH,
          mw: { ...mw },
          isFloatFrame,
          floatFrameGap,
          isCanvas,
          frameDepthUsed,
          hasStretchers: isCanvas && stretcherD > 0,
          backDepth: (() => {
            const fd = showFrame ? (isCanvas && stretcherD > 0 ? stretcherD * 0.75 : Math.min(2, effectiveFrameWidth)) : 0
            const sd = this.substrateBackData?.depth || 0
            return isCanvas && stretcherD > 0 ? stretcherD * 0.75 : (isCanvas && fd > 0 ? fd : sd > 0 ? sd : 0.1)
          })(),
        }

        this.artworkMesh = newArtworkMesh
        this.matGroup = newMatGroup
        this.frameGroup = newFrameGroup
        this.substrateBackMesh = newSubstrateBackMesh
        this.stretcherGroup = newStretcherGroup
        this.hangHardwareGroup = newHangHardwareGroup
        this.handlesGroup = newHandlesGroup
        this.dimGroup = newDimGroup

        this.sceneContentGroup.add(this.artworkMesh)
        if (this.matGroup) this.sceneContentGroup.add(this.matGroup)
        if (this.stretcherGroup) this.sceneContentGroup.add(this.stretcherGroup)
        if (this.substrateBackMesh) this.sceneContentGroup.add(this.substrateBackMesh)
        if (this.frameGroup) this.sceneContentGroup.add(this.frameGroup)
        if (this.hangHardwareGroup) this.sceneContentGroup.add(this.hangHardwareGroup)
        if (this.handlesGroup) this.sceneContentGroup.add(this.handlesGroup)
        if (this.dimGroup) this.sceneContentGroup.add(this.dimGroup)

        // Enable shadows on all meshes except resize handles (mat, stretchers, frame, etc.)
        this.sceneContentGroup.traverse((obj) => {
          if (obj.isMesh && obj.userData?.type !== 'resize-handle') {
            obj.castShadow = true
            obj.receiveShadow = true
          }
        })

        this.updateWall()

        if (!silent) {
          if (!this.hasInitialFit || this.awaitingConfig) {
            this.fitCamera()
            // Wait for Vue to update DOM and Three.js to render a frame
            await new Promise((resolve) => {
              requestAnimationFrame(() => requestAnimationFrame(resolve))
            })
            if (!this.awaitingConfig) {
              this.hasInitialFit = true
            }
          } else {
            await this.animateFitCamera()
          }
        }
      } catch (err) {
        console.error('FrameRenderer: failed to build scene', err)
      } finally {
        if (!silent && !this.awaitingConfig) {
          this.loading = false
          // The camera fit has finished and options are loaded
          this.showThumbnail = false
        }
      }
    },

    /**
     * Update scene geometry scales and positions for new dimensions.
     * No geometry creation - GPU-friendly, just transforms.
     */
    updateSceneDimensions(artW, artH) {
      if (!this.builtDims) return
      const b = this.builtDims
      const mw = this.parsedMatWidth
      const innerW = artW + mw.left + mw.right
      const innerH = artH + mw.top + mw.bottom

      if (this.artworkMesh) {
        this.artworkMesh.scale.set(artW / b.artW, artH / b.artH, 1)
        const tex = this.artworkMesh.material?.map
        if (tex) applyArtworkTextureCover(tex, artW / artH)
        const alphaMap = this.artworkMesh.material?.alphaMap
        if (alphaMap) {
          const rx = artW * 4
          const ry = artH * 4
          alphaMap.repeat.set(rx, ry)
          alphaMap.offset.set(0.5 * (1 - rx), 0.5 * (1 - ry))
        }
      }

      if (this.matGroup) {
        if (this.matGroup.userData.multi) {
          // Re-run createMultiMatBorder layout in-place so border widths stay fixed
          // while only the artwork opening grows/shrinks (group scale would stretch borders).
          const matArr = this.matDataArray || (this.matData ? [this.matData] : [])
          const widthsArr = this.matWidthsArray || []
          if (matArr.length >= 2 && widthsArr.length) {
            const reveal = inches(0.25)
            const d = inches(0.0625)
            let innerW = inches(artW)
            let innerH = inches(artH)
            let outerW = innerW
            let outerH = innerH
            for (let idx = matArr.length - 1; idx >= 0; idx--) {
              const w = widthsArr[idx] || { top: 2, bottom: 2, left: 2, right: 2 }
              const mt = inches(w.top); const mb = inches(w.bottom)
              const ml = inches(w.left); const mr = inches(w.right)
              if (idx < matArr.length - 1) { innerW = outerW; innerH = outerH }
              const xr = idx > 0 ? reveal : 0
              const fullW = innerW + ml + mr + 2 * xr
              const fullH = innerH + mt + mb + 2 * xr
              const cx = (mr - ml) / 2
              const matNum = idx + 1
              const newSizes = {
                top:    { w: fullW,       h: mt + xr,           x: cx,                              y:  innerH / 2 + (mt + xr) / 2 },
                bottom: { w: fullW,       h: mb + xr,           x: cx,                              y: -(innerH / 2 + (mb + xr) / 2) },
                left:   { w: ml + xr,     h: innerH + 2 * xr,   x: -(innerW / 2 + (ml + xr) / 2),  y: 0 },
                right:  { w: mr + xr,     h: innerH + 2 * xr,   x:  innerW / 2 + (mr + xr) / 2,    y: 0 },
              }
              this.matGroup.children.forEach((mesh) => {
                if (mesh.userData?.type === 'mat-piece' && mesh.userData.matNum === matNum) {
                  const s = newSizes[mesh.userData.side]
                  if (s && mesh.geometry && s.w > 0 && s.h > 0) {
                    mesh.geometry.dispose()
                    mesh.geometry = new THREE.BoxGeometry(s.w, s.h, d)
                    mesh.position.set(s.x, s.y, mesh.position.z)
                  }
                }
              })
              outerW = fullW; outerH = fullH
            }
          }
        } else {
          const fullW = artW + mw.left + mw.right
          const cx = (mw.right - mw.left) / 2
          const d = inches(0.0625)
          const aw = inches(artW)
          const ah = inches(artH)
          const byLabel = {
            'Mat (top)': { scale: [inches(fullW), inches(mw.top), 1], pos: [inches(cx), ah / 2 + inches(mw.top) / 2, d / 2] },
            'Mat (bottom)': { scale: [inches(fullW), inches(mw.bottom), 1], pos: [inches(cx), -(ah / 2 + inches(mw.bottom) / 2), d / 2] },
            'Mat (left)': { scale: [inches(mw.left), ah, 1], pos: [-(aw / 2 + inches(mw.left) / 2), 0, d / 2] },
            'Mat (right)': { scale: [inches(mw.right), ah, 1], pos: [aw / 2 + inches(mw.right) / 2, 0, d / 2] },
          }
          this.matGroup.traverse((obj) => {
            if (obj.geometry?.parameters && obj.userData?.type === 'mat-piece') {
              const p = obj.geometry.parameters
              const cfg = byLabel[obj.userData.label]
              if (cfg) {
                obj.scale.set(cfg.scale[0] / p.width, cfg.scale[1] / p.height, cfg.scale[2] / p.depth)
                obj.position.set(...cfg.pos)
              }
            }
          })
        }
      }

      if (this.frameGroup) {
        const frameInnerW = b.isFloatFrame ? innerW + 2 * (b.floatFrameGap || 0.25) : innerW
        const frameInnerH = b.isFloatFrame ? innerH + 2 * (b.floatFrameGap || 0.25) : innerH
        const fw = b.isFloatFrame && this.floatFrameData
          ? (parseFloat(this.floatFrameData.width) || this.frameWidth)
          : this.frameWidth
        const frameDepthVal = b.frameDepthUsed ?? Math.min(2, fw)
        const d = inches(frameDepthVal) / 2
        const iw = inches(frameInnerW)
        const ih = inches(frameInnerH)
        const offsetX = inches((mw.right - mw.left) / 2)
        const offsetY = inches((mw.top - mw.bottom) / 2)
        const frameZ = b.isCanvas ? -inches(frameDepthVal) : 0
        this.frameGroup.position.set(offsetX, offsetY, frameZ)

        this.frameGroup.traverse((obj) => {
          if (obj.userData?.type === 'frame-rail') {
            const pos = obj.userData.position
            if (pos === 'top' || pos === 'bottom') {
              updateMiteredRailGeometry(obj, frameInnerW, fw, pos, frameDepthVal, b.isFloatFrame)
              obj.position.set(0, (pos === 'top' ? 1 : -1) * ih / 2, d)
              obj.scale.y = pos === 'bottom' ? -1 : 1
            } else {
              updateMiteredRailGeometry(obj, frameInnerH, fw, pos, frameDepthVal, b.isFloatFrame)
              obj.position.set((pos === 'right' ? 1 : -1) * iw / 2, 0, d)
            }
          } else if (b.isFloatFrame && obj.userData?.type === 'frame-back-leg') {
            updateBackLegGeometry(obj, frameInnerW, frameInnerH, fw, frameDepthVal)
          }
        })

        this.builtDims.innerW = innerW
        this.builtDims.innerH = innerH
      }

      if (this.stretcherGroup) {
        this.stretcherGroup.scale.set(artW / b.artW, artH / b.artH, 1)
      }

      if (this.substrateBackMesh) {
        const sb = b.substrateBorder || 0
        if (sb > 0) {
          const denomW = b.artW + 2 * sb
          const denomH = b.artH + 2 * sb
          this.substrateBackMesh.scale.set((artW + 2 * sb) / denomW, (artH + 2 * sb) / denomH, 1)
        } else {
          this.substrateBackMesh.scale.set(artW / b.artW, artH / b.artH, 1)
        }
        const sbOffsetX = inches((mw.right - mw.left) / 2)
        const sbOffsetY = inches((mw.top - mw.bottom) / 2)
        this.substrateBackMesh.position.set(sbOffsetX, sbOffsetY, 0)
      }

      if (this.hangHardwareGroup && b.hwBuiltW != null && b.hwBuiltH != null) {
        const scaleX = (b.hasStretchers ? artW : innerW + 2) / (b.hasStretchers ? b.hwBuiltW : b.backPanelBuiltW)
        const scaleY = (b.hasStretchers ? artH : innerH + 2) / (b.hasStretchers ? b.hwBuiltH : b.backPanelBuiltH)
        this.hangHardwareGroup.scale.set(scaleX, scaleY, 1)
        const hwOffsetX = inches((mw.right - mw.left) / 2)
        const hwOffsetY = inches((mw.top - mw.bottom) / 2)
        this.hangHardwareGroup.position.set(hwOffsetX, hwOffsetY, 0)
      }

      if (this.handlesGroup) {
        const aw = inches(artW)
        const ah = inches(artH)
        const corners = [
          { x: -aw / 2, y: ah / 2 },
          { x: aw / 2, y: ah / 2 },
          { x: -aw / 2, y: -ah / 2 },
          { x: aw / 2, y: -ah / 2 },
        ]
        let i = 0
        this.handlesGroup.traverse((obj) => {
          if (obj.userData?.type === 'resize-handle') {
            obj.position.set(corners[i].x, corners[i].y, 0.5)
            i++
          }
        })
      }
    },

    /**
     * Adjust the perspective camera distance so the entire framed piece fits
     * within the container with some padding. Also resizes the wall to match.
     */
    fitCamera() {
      if (!this.threeCamera || !this.threeRenderer) return

      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })

      const container = this.$refs.container
      if (!container) return
      const rect = container.getBoundingClientRect()
      const containerW = rect.width || 400
      const containerH = rect.height || 400
      const aspect = containerW / containerH

      const sceneW = inches(outerWidth)
      const sceneH = inches(outerHeight)

      // Compute distance for telephoto perspective camera
      const fov = this.threeCamera.fov
      const dist = computeFitDistance(sceneW, sceneH, fov, aspect, 1.15)
      const targetZ = dist + this.frontZ

      this.threeCamera.aspect = aspect
      this.threeCamera.position.set(0, EYE_LEVEL, targetZ)
      this.threeCamera.lookAt(0, EYE_LEVEL, 0)
      this.threeCamera.updateProjectionMatrix()

      this.threeRenderer.setSize(containerW, containerH)
    },

    onThumbnailImageLoad(event) {
      const image = event?.target
      const naturalWidth = Number(image?.naturalWidth)
      const naturalHeight = Number(image?.naturalHeight)
      if (!Number.isFinite(naturalWidth) || !Number.isFinite(naturalHeight)) return
      if (naturalWidth <= 0 || naturalHeight <= 0) return

      if (this.threeRenderer && !this.awaitingConfig) {
        this.fitCamera()
      }
    },

    /** Animate camera distance from current to fitted over duration ms. No-ops if already at target. */
    animateFitCamera(duration = 1000) {
      if (!this.threeCamera || !this.$refs.container) return Promise.resolve()

      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })

      const rect = this.$refs.container.getBoundingClientRect()
      const aspect = (rect.width || 400) / (rect.height || 400)
      const sceneW = inches(outerWidth)
      const sceneH = inches(outerHeight)
      const fov = this.threeCamera.fov

      const targetZ = computeFitDistance(sceneW, sceneH, fov, aspect, 1.15) + this.frontZ
      const startZ = this.threeCamera.position.z

      // Skip animation if already within 1% of target
      if (Math.abs(targetZ - startZ) < targetZ * 0.01) return Promise.resolve()

      return new Promise((resolve) => {
        const startTime = performance.now()

        const animate = (now) => {
          const t = Math.min((now - startTime) / duration, 1)
          const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

          this.threeCamera.position.z = startZ + (targetZ - startZ) * eased
          this.threeCamera.updateProjectionMatrix()

          if (t < 1) {
            requestAnimationFrame(animate)
          } else {
            resolve()
          }
        }
        requestAnimationFrame(animate)
      })
    },

    // ------------------------------------------------------------------
    // Render loop
    // ------------------------------------------------------------------

    startRenderLoop() {
      const render = () => {
        this.animFrameId = requestAnimationFrame(render)
        if (this.threeRenderer && this.threeScene && this.threeCamera) {
          if (this.sceneContentGroup) {
            this.sceneContentGroup.rotation.y = this.flipRotation
            this.sceneContentGroup.position.z = this.flipZOffset || 0
          }
          this.threeRenderer.render(this.threeScene, this.threeCamera)
        }
      }
      render()
    },

    stopRenderLoop() {
      if (this.animFrameId) {
        cancelAnimationFrame(this.animFrameId)
        this.animFrameId = null
      }
    },

    /**
     * Animate flip to show back of frame (180° Y rotation).
     * Two-phase: first detach from wall (translate +Z), then rotate.
     */
    flipToBack() {
      if (this.flipAnimating) return
      this.flipAnimating = true
      const duration = 2000
      const detachPhase = 0.35
      const startRotation = this.flipRotation
      const startZ = this.flipZOffset
      let clearance = 0
      if (this.wallEnabled && this.builtDims) {
        const { outerWidth, outerHeight } = computeOuterDimensions({
          artWidth: this.builtDims.artW,
          artHeight: this.builtDims.artH,
          matTop: this.builtDims.mw?.top ?? 0,
          matBottom: this.builtDims.mw?.bottom ?? 0,
          matLeft: this.builtDims.mw?.left ?? 0,
          matRight: this.builtDims.mw?.right ?? 0,
          frameWidth: this.frameWidth,
          substrateBorder: this.builtDims.substrateBorder,
        })
        const halfW = inches(outerWidth) / 2
        const halfH = inches(outerHeight) / 2
        const backDepth = this.builtDims.backDepth ?? 2
        const halfExtent = Math.max(halfW, halfH)
        clearance = Math.sqrt(halfExtent * halfExtent + backDepth * backDepth) - backDepth + 1
      }
      const startTime = performance.now()

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1)

        if (t <= detachPhase) {
          const p = t / detachPhase
          const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          this.flipZOffset = startZ + clearance * ep
          this.flipRotation = startRotation
        } else {
          this.flipZOffset = startZ + clearance
          const p = (t - detachPhase) / (1 - detachPhase)
          const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          this.flipRotation = startRotation + (Math.PI - startRotation) * ep
        }

        if (t < 1) requestAnimationFrame(animate)
        else {
          this.flipAnimating = false
          this.$emit('flipped', { showingBack: true })
        }
      }
      requestAnimationFrame(animate)
    },

    /**
     * Animate flip to show front of frame (0° Y rotation).
     * Two-phase: first rotate back, then reattach to wall (translate -Z).
     */
    flipToFront() {
      if (this.flipAnimating) return
      this.flipAnimating = true
      const duration = 2000
      const rotatePhase = 0.65
      const startRotation = this.flipRotation
      const startZ = this.flipZOffset
      const startTime = performance.now()

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1)

        if (t <= rotatePhase) {
          const p = t / rotatePhase
          const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          this.flipRotation = startRotation + (0 - startRotation) * ep
          this.flipZOffset = startZ
        } else {
          this.flipRotation = 0
          const p = (t - rotatePhase) / (1 - rotatePhase)
          const ep = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          this.flipZOffset = startZ * (1 - ep)
        }

        if (t < 1) requestAnimationFrame(animate)
        else {
          this.flipAnimating = false
          this.$emit('flipped', { showingBack: false })
        }
      }
      requestAnimationFrame(animate)
    },

    /** Toggle between front and back view. */
    toggleFlip() {
      const showingBack = this.flipRotation > Math.PI / 2
      if (showingBack) this.flipToFront()
      else this.flipToBack()
    },

    // ------------------------------------------------------------------
    // Interaction handlers
    // ------------------------------------------------------------------

    getMouseNDC(event) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      return new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      )
    },

    onMouseMove(event) {
      if (!this.interactive || !this.threeRaycaster || !this.threeCamera) return

      const ndc = this.getMouseNDC(event)
      this.threeRaycaster.setFromCamera(ndc, this.threeCamera)

      // Dragging is handled by document mousemove (tracks beyond canvas)
      if (this.isDragging) return

      // Collect all meshes for raycasting
      const objects = []
      this.threeScene.traverse((obj) => {
        if (obj.isMesh && obj.userData.type) {
          objects.push(obj)
        }
      })

      const intersects = this.threeRaycaster.intersectObjects(objects)

      if (this.hoveredObject) {
        this.onUnhover(this.hoveredObject)
      }

      if (intersects.length > 0) {
        const hit = intersects[0].object
        this.hoveredObject = hit
        this.onHover(hit, event)
        this.updateHandlesVisibility(hit)
      } else {
        this.tooltipVisible = false
        this.showDimensions = false
        this.$refs.container.style.cursor = 'default'
        this.hoveredObject = null
        this.updateHandlesVisibility(null)
      }
    },

    onHover(mesh, event) {
      const data = mesh.userData
      if (!data) return

      const rect = this.$refs.container.getBoundingClientRect()
      this.tooltipX = event.clientX - rect.left + 12
      this.tooltipY = event.clientY - rect.top - 8

      if (data.type === 'resize-handle') {
        this.$refs.container.style.cursor = this.getCursorForCorner(data.corner)
        mesh.material.opacity = 0.95
        this.tooltipText = 'Drag to resize'
        this.tooltipVisible = true
        return
      }

      this.$refs.container.style.cursor = 'default'

      if (data.type === 'artwork') {
        this.tooltipText = `${this.parsedArtWidth}" × ${this.parsedArtHeight}"`
        this.tooltipVisible = true
        this.showDimensionLabels()
      } else if (data.type === 'frame-rail') {
        this.tooltipText = `Frame: ${data.widthInches}" wide`
        this.tooltipVisible = true
      } else if (data.type === 'mat-piece') {
        const num = data.matNum != null && data.matNum !== '' ? data.matNum : null
        const side = data.side || data.label || 'border'
        const w = data.widthInches != null && !Number.isNaN(Number(data.widthInches))
          ? Number(data.widthInches)
          : null
        this.tooltipText = (num != null && w != null)
          ? `Mat ${num}: ${w}" ${side}`
          : (w != null ? `Mat: ${w}" ${side}` : `Mat ${side}`)
        this.tooltipVisible = true
      } else {
        this.tooltipVisible = false
      }
    },

    onUnhover() {
      // Handle visibility is managed by updateHandlesVisibility
    },

    /** Show/hide resize handles based on hover state. Handles visible only when hovering framed image. */
    updateHandlesVisibility(hoveredMesh) {
      if (!this.handlesGroup) return
      const show = this.isDragging || (hoveredMesh && (
        hoveredMesh.userData?.type === 'artwork' ||
        hoveredMesh.userData?.type === 'frame-rail' ||
        hoveredMesh.userData?.type === 'mat-piece' ||
        hoveredMesh.userData?.type === 'resize-handle'
      ))
      const baseOpacity = show ? 0.6 : 0
      const hoveredHandle = hoveredMesh?.userData?.type === 'resize-handle' ? hoveredMesh : null
      this.handlesGroup.traverse((obj) => {
        if (obj.isMesh && obj.userData?.type === 'resize-handle') {
          obj.material.opacity = (obj === hoveredHandle) ? 0.95 : baseOpacity
        }
      })
    },

    showDimensionLabels() {
      if (!this.threeCamera || !this.threeRenderer) return

      this.showDimensions = true
      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })
      this.widthLabel = `${outerWidth}"`
      this.heightLabel = `${outerHeight}"`

      const outerW = inches(outerWidth)
      const outerH = inches(outerHeight)
      const labelOffset = 0.5

      // Project 3D points to screen (position labels at outer edges of framed piece)
      const widthMid = new THREE.Vector3(0, -outerH / 2 - labelOffset, 0)
      const heightMid = new THREE.Vector3(outerW / 2 + labelOffset, 0, 0)

      const rect = this.$refs.container.getBoundingClientRect()
      const w = rect.width
      const h = rect.height

      widthMid.project(this.threeCamera)
      this.widthLabelPos = {
        x: (widthMid.x * 0.5 + 0.5) * w,
        y: (-widthMid.y * 0.5 + 0.5) * h,
      }

      heightMid.project(this.threeCamera)
      this.heightLabelPos = {
        x: (heightMid.x * 0.5 + 0.5) * w,
        y: (-heightMid.y * 0.5 + 0.5) * h,
      }
    },

    getCursorForCorner(corner) {
      const cursors = {
        'top-left': 'nwse-resize',
        'top-right': 'nesw-resize',
        'bottom-left': 'nesw-resize',
        'bottom-right': 'nwse-resize',
      }
      return cursors[corner] || 'pointer'
    },

    onMouseDown(event) {
      if (!this.interactive || !this.hoveredObject) return
      const data = this.hoveredObject.userData
      if (data.type !== 'resize-handle') return

      if (this.debouncedRebuild?.cancel) this.debouncedRebuild.cancel()
      this.isDragging = true
      this.dragHandle = data.corner
      this.dragStartPos = { x: event.clientX, y: event.clientY }
      this.dragStartDims = {
        width: this.parsedArtWidth,
        height: this.parsedArtHeight,
      }
      document.addEventListener('mousemove', this.boundHandleDrag)
      document.addEventListener('mouseup', this.boundEndResizeDrag)
      document.body.style.cursor = this.getCursorForCorner(data.corner)
      document.body.style.userSelect = 'none'
      event.preventDefault()
    },

    onMouseUp() {
      this.endResizeDrag()
    },

    endResizeDrag() {
      if (!this.isDragging) return
      document.removeEventListener('mousemove', this.boundHandleDrag)
      document.removeEventListener('mouseup', this.boundEndResizeDrag)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      this.isDragging = false
      this.dragHandle = null
      this.dragStartPos = null
      this.dragStartDims = null
      this.dragWidth = null
      this.dragHeight = null
      // Flush any pending RAF so final dimensions are applied/emitted before rebuild
      if (this.dragRafId != null) {
        cancelAnimationFrame(this.dragRafId)
        this.dragRafId = null
        if (this.pendingDragDims) {
          this.updateSceneDimensions(this.pendingDragDims.width, this.pendingDragDims.height)
          this.$emit('resize', this.pendingDragDims)
          this.pendingDragDims = null
        }
      }
      // Parent prop updates from emitted resize will trigger a single rebuild via watchers.
      // Avoid forcing an immediate rebuild+fit here, which causes visible double-snap.
    },

    onMouseLeave() {
      this.tooltipVisible = false
      this.showDimensions = false
      if (this.hoveredObject) {
        this.onUnhover(this.hoveredObject)
        this.hoveredObject = null
      }
      this.updateHandlesVisibility(null)
      if (!this.isDragging) this.$refs.container.style.cursor = 'default'
    },

    handleDrag(event) {
      if (!this.dragStartPos || !this.dragStartDims) return

      const dx = event.clientX - this.dragStartPos.x
      const dy = event.clientY - this.dragStartPos.y

      // Convert pixel delta to inch delta (use canvas rect to match NDC/raycasting)
      const canvas = this.$refs.canvas
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      // PerspectiveCamera: derive scene-units-per-pixel from FOV + camera distance to z=0
      const camDist = this.threeCamera.position.z
      const fovRad = THREE.MathUtils.degToRad(this.threeCamera.fov)
      const sceneH = 2 * camDist * Math.tan(fovRad / 2)
      const sceneW = sceneH * this.threeCamera.aspect
      const trackFactor = 2
      const pxToInchX = (sceneW / rect.width) * trackFactor
      const pxToInchY = (sceneH / rect.height) * trackFactor

      let newWidth = this.dragStartDims.width
      let newHeight = this.dragStartDims.height

      const corner = this.dragHandle
      const dxInches = dx * pxToInchX
      const dyInches = dy * pxToInchY

      if (corner === 'top-right' || corner === 'bottom-right') {
        newWidth += dxInches
      } else {
        newWidth -= dxInches
      }
      if (corner === 'bottom-left' || corner === 'bottom-right') {
        newHeight += dyInches
      } else {
        newHeight -= dyInches
      }

      newWidth = Math.max(1, Math.round(newWidth * 4) / 4)
      newHeight = Math.max(1, Math.round(newHeight * 4) / 4)

      this.dragWidth = newWidth
      this.dragHeight = newHeight
      if (this.dimGroup) this.dimGroup.visible = false

      // RAF-throttle: batch mousemoves into one update per frame (avoids 4 ExtrudeGeometry allocs per mousemove)
      this.pendingDragDims = { width: newWidth, height: newHeight }
      if (this.dragRafId == null) {
        this.dragRafId = requestAnimationFrame(() => this.applyPendingDragDims())
      }
    },

    applyPendingDragDims() {
      this.dragRafId = null
      const d = this.pendingDragDims
      this.pendingDragDims = null
      if (!d) return
      this.updateSceneDimensions(d.width, d.height)
      // Skip emit if unchanged (reduces Vue reactivity churn during drag)
      if (!this.lastEmittedDims || this.lastEmittedDims.width !== d.width || this.lastEmittedDims.height !== d.height) {
        this.lastEmittedDims = d
        this.$emit('resize', { width: d.width, height: d.height })
      }
    },

    // ------------------------------------------------------------------
    // Resize handling
    // ------------------------------------------------------------------

    onResize() {
      this.fitCamera()
    },

    // ------------------------------------------------------------------
    // Export
    // ------------------------------------------------------------------

    /**
     * Export a uniform thumbnail of the 3D canvas.
     * The edge of the frame will be the edge of the image.
     * @param {number} [maxDim=800] - Maximum dimension in pixels
     * @returns {string} data URL
     */
    exportThumbnail(maxDim = 800) {
      if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return null

      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })

      const sceneW = inches(outerWidth)
      const sceneH = inches(outerHeight)
      const frameAspect = sceneW / sceneH

      // Calculate pixel dimensions (max 800px)
      let thumbW, thumbH
      if (frameAspect > 1) {
        thumbW = maxDim
        thumbH = Math.round(maxDim / frameAspect)
      } else {
        thumbH = maxDim
        thumbW = Math.round(maxDim * frameAspect)
      }

      // Save current state
      const container = this.$refs.container
      const rect = container.getBoundingClientRect()
      const origW = rect.width || 400
      const origH = rect.height || 400
      const origAspect = this.threeCamera.aspect
      const origZ = this.threeCamera.position.z
      const origPixelRatio = this.threeRenderer.getPixelRatio()

      // Temporarily adjust camera and renderer for thumbnail
      this.threeCamera.aspect = frameAspect
      // Use padding = 1.0 so the frame exactly touches the edges
      const dist = computeFitDistance(sceneW, sceneH, this.threeCamera.fov, frameAspect, 1.0)
      this.threeCamera.position.z = dist + this.frontZ
      this.threeCamera.updateProjectionMatrix()

      // Force pixel ratio to 1 so the output is exactly maxDim pixels
      this.threeRenderer.setPixelRatio(1)
      // Use false to prevent CSS layout shifts
      this.threeRenderer.setSize(thumbW, thumbH, false)

      // Ensure rotation is 0 (front view) for thumbnail
      const origRotation = this.sceneContentGroup ? this.sceneContentGroup.rotation.y : 0
      const origZOffset = this.sceneContentGroup ? this.sceneContentGroup.position.z : 0
      if (this.sceneContentGroup) {
        this.sceneContentGroup.rotation.y = 0
        this.sceneContentGroup.position.z = 0
      }

      // Render and capture
      this.threeRenderer.render(this.threeScene, this.threeCamera)
      const dataURL = this.threeRenderer.domElement.toDataURL('image/jpeg', 0.9)

      // Restore original state
      if (this.sceneContentGroup) {
        this.sceneContentGroup.rotation.y = origRotation
        this.sceneContentGroup.position.z = origZOffset
      }
      this.threeCamera.aspect = origAspect
      this.threeCamera.position.z = origZ
      this.threeCamera.updateProjectionMatrix()
      this.threeRenderer.setPixelRatio(origPixelRatio)
      this.threeRenderer.setSize(origW, origH, false)

      return dataURL
    },

    /**
     * Export the current render as a PNG data URL.
     * @param {number} [scale=2] - Resolution multiplier
     * @returns {string} data URL
     */
    exportDataURL(scale = 2) {
      if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return null

      const container = this.$refs.container
      const rect = container.getBoundingClientRect()
      const w = Math.round(rect.width * scale)
      const h = Math.round(rect.height * scale)

      // Temporarily resize renderer for high-res export
      this.threeRenderer.setSize(w, h)
      this.threeRenderer.render(this.threeScene, this.threeCamera)
      const dataURL = this.threeRenderer.domElement.toDataURL('image/png')

      // Restore original size
      this.threeRenderer.setSize(rect.width, rect.height)

      return dataURL
    },

    /**
     * Export as a Blob (for upload or download).
     * @param {number} [scale=2]
     * @returns {Promise<Blob>}
     */
    async exportBlob(scale = 2) {
      const dataURL = this.exportDataURL(scale)
      if (!dataURL) return null
      const res = await fetch(dataURL)
      return res.blob()
    },

    /**
     * Trigger a browser download of the rendered image.
     * @param {string} [filename='frame-render.png']
     * @param {number} [scale=2]
     */
    downloadImage(filename = 'frame-render.png', scale = 2) {
      const dataURL = this.exportDataURL(scale)
      if (!dataURL) return
      const link = document.createElement('a')
      link.href = dataURL
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.frame-renderer {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.frame-renderer__canvas-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.frame-renderer canvas {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

.frame-renderer__tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 0.75rem;
  padding: 3px 8px;
  border-radius: 3px;
  white-space: nowrap;
  z-index: 10;
  transform: translateY(-100%);
}

.frame-renderer__dimensions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
}

.frame-renderer__dim-label {
  position: absolute;
  font-size: 0.7rem;
  font-weight: 600;
  color: #1976D2;
  background: rgba(255,255,255,0.85);
  padding: 1px 5px;
  border-radius: 2px;
  transform: translate(-50%, -50%);
  white-space: nowrap;
}

.frame-renderer__loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.frame-renderer__thumbnail {
  width: auto;
  max-width: calc(100% / 1.15);
  max-height: calc(100% / 1.15);
  display: block;
}
</style>
